import type {
  AiAssistantSource,
  AiProvenanceItem,
} from '@asset-metrix/portal-ui';

import type { DocumentChatEvent } from '../../api/portalGateway/documentChat';

type CitationsEvent = Extract<DocumentChatEvent, { type: 'citations' }>;

function parseCitationNumber(label: string): number | null {
  const match = /^\[(\d+)]$/.exec(label);
  const number = match ? Number(match[1]) : 0;
  return number > 0 ? number : null;
}

function mapProvenance(
  provenance: CitationsEvent['items'][number]['excerpts'][number]['provenance']
): AiProvenanceItem[] {
  return provenance.map((item) => {
    const bbox = item.bbox;
    return {
      page_no: item.page_no,
      bbox: bbox
        ? {
            l: bbox[0],
            t: bbox[1],
            r: bbox[2],
            b: bbox[3],
            coord_origin: 'BOTTOMLEFT',
          }
        : undefined,
      charspan: item.charspan,
      label: item.label,
    };
  });
}

function collectPages(
  excerpt: CitationsEvent['items'][number]['excerpts'][number]
): number[] {
  const pages = new Set<number>();
  const start = excerpt.page;
  const end = excerpt.page_end ?? excerpt.page;
  if (typeof start === 'number' && typeof end === 'number') {
    for (
      let page = Math.min(start, end);
      page <= Math.max(start, end);
      page += 1
    ) {
      pages.add(page);
    }
  }
  for (const item of excerpt.provenance ?? []) {
    pages.add(item.page_no);
  }
  return [...pages].sort((a, b) => a - b);
}

export function mapCitationSources(event: CitationsEvent): AiAssistantSource[] {
  const sources = new Map<number, AiAssistantSource>();

  for (const document of event.items) {
    for (const excerpt of document.excerpts) {
      const citationNumber = parseCitationNumber(excerpt.label);
      if (citationNumber == null || sources.has(citationNumber)) {
        continue;
      }

      sources.set(citationNumber, {
        kind: 'document',
        citationNumber,
        documentId: document.doc_id,
        label: document.doc_name,
        pages: collectPages(excerpt),
        provenance: mapProvenance(excerpt.provenance),
        sourceIndex: document.source_index,
        excerpt: excerpt.citation_preview,
      });
    }
  }

  return [...sources.values()].sort(
    (a, b) => (a.citationNumber ?? 0) - (b.citationNumber ?? 0)
  );
}

function isCitationDocument(
  value: Record<string, unknown>
): value is CitationsEvent['items'][number] {
  return (
    typeof value.doc_id === 'string' &&
    typeof value.doc_name === 'string' &&
    Array.isArray(value.excerpts)
  );
}

export function mapStoredCitations(
  raw: Record<string, unknown>[] | null | undefined
): AiAssistantSource[] {
  if (!raw?.length) {
    return [];
  }
  const items = raw.filter(isCitationDocument);
  if (items.length === 0) {
    return [];
  }
  return mapCitationSources({ type: 'citations', items });
}

export function toInlineCitationTokens(
  text: string,
  sources: readonly AiAssistantSource[]
): string {
  const numbers = new Set(
    sources.map((source, index) => source.citationNumber ?? index + 1)
  );
  return text.replace(/(?<!\[)\[(\d+)](?!])/g, (match, digits: string) => {
    const number = Number(digits);
    return numbers.has(number) ? `[[${number}]]` : match;
  });
}
